import React, { useState, useCallback } from 'react';
import { Navigate, useSearchParams, Link as RouterLink } from 'react-router-dom';
import { Box, Container, Typography, Link } from '@mui/material';
import { confirmResetPassword } from '@aws-amplify/auth';
import { ResetPasswordForm } from '../../components/ResetPasswordForm';
import { useAuth } from '../../contexts/AuthContext';

export const ResetPasswordPage: React.FC = () => {
  const { isAuthenticated, login } = useAuth();
  const [searchParams] = useSearchParams();
  const defaultEmail = searchParams.get('email') || '';

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const clearError = useCallback(() => setError(null), []);

  const handleResetPassword = useCallback(async (email: string, code: string, newPassword: string) => {
    setIsLoading(true);
    setError(null);
    try {
      await confirmResetPassword({
        username: email,
        confirmationCode: code,
        newPassword,
      });

      // Auto sign-in after successful reset using context login
      // login() clears requiresPasswordReset state and handles full auth flow
      await login(email, newPassword);
    } catch (err: any) {
      let errorMessage = 'Failed to reset password';

      if (err?.name === 'CodeMismatchException') {
        errorMessage = 'Invalid verification code. Please check and try again.';
      } else if (err?.name === 'ExpiredCodeException') {
        errorMessage = 'Verification code has expired. Please request a new one.';
      } else if (err?.name === 'InvalidPasswordException') {
        errorMessage = 'Password does not meet requirements.';
      } else if (err?.name === 'LimitExceededException') {
        errorMessage = 'Too many attempts. Please try again later.';
      } else if (err?.name === 'UserNotFoundException') {
        errorMessage = 'No account found with this email address.';
      } else if (err?.message) {
        errorMessage = err.message;
      }

      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, [login]);

  if (isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  return (
    <Box minHeight="100vh" bgcolor="background.default" py={4}>
      <Container maxWidth="lg">
        <Box textAlign="center" mb={4}>
          <Typography variant="h3" component="h1" color="text.primary" gutterBottom>
            Tax Assist by Harness
          </Typography>
          <Typography variant="subtitle1" color="text.secondary">
            Reset your password using the code sent to your email
          </Typography>
        </Box>
        <ResetPasswordForm
          onResetPassword={handleResetPassword}
          isLoading={isLoading}
          error={error}
          onClearError={clearError}
          defaultEmail={defaultEmail}
        />
        <Box textAlign="center" mt={2}>
          <Link component={RouterLink} to="/login" variant="body2">
            Back to Sign In
          </Link>
        </Box>
      </Container>
    </Box>
  );
};
